import { useEffect, useRef, useState } from "react";
import api, { API } from "../lib/api";
import { Sparkles, Send, Loader2, Plus, Trash2, MessageSquare, Pin } from "lucide-react";
import { toast } from "sonner";
import { AiResult } from "../components/AiResultView";

const SUGGESTIONS = [
  "Show all non-LR expenses in June 2026",
  "What percentage of June 2026 expenses were non-LR? Pie chart.",
  "Total expenses by category this year",
  "Monthly freight from LRs in 2026 as a line chart",
];

export default function Assistant() {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [pinning, setPinning] = useState(null);
  const endRef = useRef(null);

  const loadSessions = () => api.get("/ai/sessions").then((r) => setSessions(r.data)).catch(() => {});
  useEffect(() => { loadSessions(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  function openSession(id) {
    setSessionId(id);
    api.get(`/ai/sessions/${id}/messages`).then((r) => setMessages(r.data)).catch(() => {});
  }
  function newChat() { setSessionId(null); setMessages([]); setInput(""); }

  async function send(text) {
    const q = (text ?? input).trim();
    if (!q || sending) return;
    setInput("");
    setMessages((m) => [...m, { id: `u-${Date.now()}`, role: "user", content: q }]);
    setSending(true);
    try {
      const { data } = await api.post("/ai/chat", { session_id: sessionId, message: q });
      if (!sessionId) { setSessionId(data.session_id); loadSessions(); }
      setMessages((m) => [...m, {
        id: data.message_id, role: "assistant", content: data.answer, title: data.title,
        columns: data.columns, rows: data.rows, chart: data.chart, row_count: data.row_count,
      }]);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Try rephrasing.";
      setMessages((m) => [...m, { id: `e-${Date.now()}`, role: "assistant", content: msg, error: true }]);
    } finally { setSending(false); }
  }

  async function pin(m) {
    setPinning(m.id);
    try {
      await api.post("/ai/pins", { message_id: m.id });
      toast.success("Pinned to Dashboard");
    } catch { toast.error("Could not pin this answer"); }
    finally { setPinning(null); }
  }

  async function removeSession(id, e) {
    e.stopPropagation();
    if (!window.confirm("Delete this conversation?")) return;
    await api.delete(`/ai/sessions/${id}`);
    if (id === sessionId) newChat();
    loadSessions();
  }

  return (
    <div className="flex gap-6 h-[calc(100vh-8rem)]" data-testid="assistant-page">
      {/* Sessions sidebar */}
      <aside className="w-60 shrink-0 hidden lg:flex flex-col border-r border-[#E5E3DB] pr-4">
        <button onClick={newChat} className="btn-primary w-full mb-3" data-testid="new-chat-btn"><Plus className="w-4 h-4" /> New chat</button>
        <div className="overflow-y-auto space-y-1">
          {sessions.map((s) => (
            <div key={s.id} onClick={() => openSession(s.id)}
              className={`group flex items-center gap-2 px-2 py-2 cursor-pointer text-sm ${s.id === sessionId ? "bg-[#F5F3EC] text-[#1A1A18]" : "text-[#68665E] hover:bg-[#F5F3EC]"}`}
              data-testid={`session-${s.id}`}>
              <MessageSquare className="w-4 h-4 shrink-0" />
              <span className="truncate flex-1">{s.title || "Untitled"}</span>
              <button onClick={(e) => removeSession(s.id, e)} className="opacity-0 group-hover:opacity-100 text-[#8A2526]" data-testid={`session-delete-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      </aside>

      {/* Chat */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-6 h-6 text-[#C84B31]" />
          <div>
            <h1 className="font-headings text-2xl font-bold text-[#1A1A18]">Analytics Assistant</h1>
            <p className="text-xs text-[#68665E]">Ask about expenses, LRs, invoices & quotations — get tables, charts and Excel.</p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto space-y-4 pr-1" data-testid="chat-thread">
          {messages.length === 0 && (
            <div className="text-center mt-10">
              <p className="text-sm text-[#68665E] mb-4">Try asking:</p>
              <div className="flex flex-wrap gap-2 justify-center max-w-2xl mx-auto">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)} className="px-3 py-2 text-sm border border-[#E5E3DB] hover:bg-[#F5F3EC] text-[#1A1A18] rounded-full" data-testid="suggestion-chip">{s}</button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
              {m.role === "user" ? (
                <div className="bg-[#1A1A18] text-white px-4 py-2 rounded-2xl rounded-br-sm max-w-[80%] text-sm" data-testid="msg-user">{m.content}</div>
              ) : (
                <div className={`max-w-full w-full ${m.error ? "text-[#8A2526]" : ""}`} data-testid="msg-assistant">
                  {m.title && <div className="font-semibold text-[#1A1A18] mb-1">{m.title}</div>}
                  {m.content && <p className="text-sm text-[#3A3A34] mb-3">{m.content}</p>}
                  {m.columns?.length > 0 && (
                    <>
                      <AiResult columns={m.columns} rows={m.rows} chart={m.chart} rowCount={m.row_count} exportUrl={`${API}/ai/messages/${m.id}/export.xlsx`} />
                      <button onClick={() => pin(m)} disabled={pinning === m.id}
                        className="mt-2 text-xs inline-flex items-center gap-1 text-[#C84B31] hover:underline disabled:opacity-50" data-testid="pin-report-btn">
                        {pinning === m.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Pin className="w-3.5 h-3.5" />} Pin to Dashboard
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
          {sending && <div className="flex items-center gap-2 text-sm text-[#68665E]" data-testid="assistant-thinking"><Loader2 className="w-4 h-4 animate-spin" /> Analyzing your data…</div>}
          <div ref={endRef} />
        </div>

        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-4 flex gap-2 border-t border-[#E5E3DB] pt-4">
          <input value={input} onChange={(e) => setInput(e.target.value)} disabled={sending}
            placeholder="Ask about your data, or refine the last answer (e.g. 'add the reason column')…"
            className="input-field flex-1" data-testid="chat-input" />
          <button type="submit" disabled={sending || !input.trim()} className="btn-primary" data-testid="chat-send-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </form>
      </div>
    </div>
  );
}
